import { ThemeProvider } from 'styled-components';
import { GlobalStyles } from './global';
import { theme } from './theme';
import { useContext, useEffect, useState } from 'react';
import { BrowserProvider } from 'ethers';
import {
  App,
  Viewer,
  Broadside,
  Manual,
  Mint,
  Impact,
  TOS,
  PP,
  viewerLoader,
  AuthRedirect,
  StripeRedirect,
  Missions,
  Invite,
  inviteLoader,
  Flex,
  flexLoader
} from './routes';
import { LoadingSpinner } from './components';
import { AppContextProvider, AppContext } from './providers/AppContext';
import { AuthProvider } from './providers/AuthProvider';
import { Account, Home } from './pages';
import { DiscordSdkProvider } from './providers/DiscordSdkProvider';
import { ShopProvider } from './providers/ShopProvider';
import { AudioProvider } from './providers/AudioContext';

import { createWeb3Modal } from '@web3modal/wagmi/react'
import { defaultWagmiConfig } from '@web3modal/wagmi/react/config'
import { WagmiProvider, http, fallback } from 'wagmi'
import { mainnet, immutableZkEvm, immutableZkEvmTestnet } from 'wagmi/chains';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { DISCORD_APP_ID, WHITEPAPER_URL, IS_PRODUCTION } from './constants';
import { ArweaveWalletKit } from "arweave-wallet-kit";
import { passportInstance } from './utils';

import {
  createBrowserRouter,
  RouterProvider,
  Link
} from "react-router-dom";

const arConnectConfig = {
  permissions: [
    "ACCESS_ADDRESS",
    "ACCESS_PUBLIC_KEY",
    "SIGN_TRANSACTION",
    "SIGNATURE",
    "DISPATCH",
  ],
  ensurePermissions: true,
};

const chains = [mainnet, immutableZkEvm];
const projectId = 'b7fa64bece4cf45bfad47f013129c43c';
const metadata = {
  name: 'BaseJump',
  description: 'BaseJump',
  url: window.location.protocol + '//' + window.location.host,
  icons: ['https://images.squarespace-cdn.com/content/v1/65bb9799d452301e910b14dd/907a6c3a-828e-4e7c-ae1b-088e3bff3792/bjlogo.png?format=500w']
}

const config = defaultWagmiConfig({
  chains,
  projectId,
  metadata,
  transports: {
    [mainnet.id]: http(`https://rpc.walletconnect.com/v1/?chainId=eip155:1&projectId=${projectId}`),
    [immutableZkEvm.id]: http()
  },
  coinbasePreference: 'smartWalletOnly'
})

createWeb3Modal({
  wagmiConfig: config,
  projectId,
  featuredWalletIds: ['fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa']
})

const queryClient = new QueryClient()

const AccountWrapper = () => {
  const { accountAddress } = useContext(AppContext);
  return <Account accountAddress={accountAddress} />;
};


const Whitepaper = () => {
  window.location.href = WHITEPAPER_URL;
  return null;
};

export const PassportRedirect = () => {
  useEffect(async () => {
    passportInstance.loginCallback()
      .then(() => {
        if (window.opener) {
          window.opener.postMessage({ passportAuthComplete: true });
          window.close();
        }
      })
      .catch((error) => {
        console.error('Error in login callback:', error);
      });
  }, []);

  return (
    <div className='page'>
      <LoadingSpinner />
    </div>
  );
};

export default function AppWrapper() {
  const router = createBrowserRouter([
    { path: "/", element: (<App />), children: [
      { path: "broadside", element: (<Broadside />)},
      { path: "app", element: (<AccountWrapper />)},
      { path: "home", element: (<Home />)},
      { path: "mint", element: (<Mint />)},
      { path: "impact", element: <Impact /> },
      { path: "missions", element: <Missions /> },
    ] },
    { path: "manual", element: (<Manual />)},
    { path: "passport", element: (<PassportRedirect />)},
    { path: "auth", element: (<AuthRedirect />)},
    { path: "stripe_redirect", element: (<StripeRedirect />)},
    { path: "terms", element: (<TOS />)},
    { path: "privacy", element: (<PP />)},
    { path: "viewer/:glbUrl", element: (<Viewer />), loader: viewerLoader },
    { path: "flex/:avatarId", element: (<Flex />), loader: flexLoader },
    { path: "invite/:invitationCode", element: (<Invite />), loader: inviteLoader },
    { path: "action", element: <Whitepaper /> },
  ]);

  const [browserProvider, setBrowserProvider] = useState(null);
  useEffect(() => {
    const init = async () => {
      const passportProvider = await passportInstance.connectEvm();
      setBrowserProvider(new BrowserProvider(passportProvider));
    }

    init();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <>
	<WagmiProvider config={config}>
        <ArweaveWalletKit config={arConnectConfig}>
	  <QueryClientProvider client={queryClient}>
	    <AuthProvider>
              <DiscordSdkProvider discordAppId={DISCORD_APP_ID}>
                <AppContextProvider browserProvider={browserProvider}>
                  <AudioProvider>
                  <ShopProvider>
                    <GlobalStyles />
                    <RouterProvider router={router} />
                  </ShopProvider>
                  </AudioProvider>
                </AppContextProvider>
              </DiscordSdkProvider>
	    </AuthProvider>
	  </QueryClientProvider>
        </ArweaveWalletKit>
        </WagmiProvider>
      </>
    </ThemeProvider>
  );
}
